import { useEffect } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PageLoader } from "@/components/ui/spinner";
import { isVerificationPending, useLogin, useSession } from "@/lib/auth";
import { safeReturnPath } from "@/lib/return-path";
import shiftLogoWhite from "@/assets/shift-logo-white.png";
import shiftLogoInk from "@/assets/shift-logo-ink.png";

const schema = z.object({
  email: z.string().email("Enter a valid email"),
  password: z.string().min(1, "Password is required"),
});

type FormValues = z.infer<typeof schema>;

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const session = useSession();
  const login = useLogin();
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "" },
  });

  useEffect(() => {
    if (!session.data?.user) return;
    const u = session.data.user;
    const elevated = u.is_platform_admin || u.org_role === "org_admin";
    const needsStaging = !u.organization_id && !elevated;
    const from = (location.state as {
      from?: { pathname?: string; search?: string; hash?: string };
    } | null)?.from;
    const stateReturnPath = from?.pathname
      ? `${from.pathname}${from.search ?? ""}${from.hash ?? ""}`
      : null;
    const returnPath =
      safeReturnPath(stateReturnPath) ??
      safeReturnPath(searchParams.get("next")) ??
      "/dashboard";
    navigate(needsStaging ? "/staging" : returnPath, { replace: true });
  }, [session.data, navigate, location.state, searchParams]);

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await login.mutateAsync(values);
    } catch (err) {
      form.setError("password", {
        message: isVerificationPending(err)
          ? "Your email isn't verified yet — check your inbox for the verification link."
          : err instanceof Error
            ? err.message
            : "Sign in failed",
      });
    }
  });

  // While the session is resolving — or once we know the user is signed in and
  // the redirect above is firing — don't render the login form. Otherwise an
  // already-authenticated user flashes the form before being redirected.
  if (session.isLoading || session.data?.user) {
    return <PageLoader className="min-h-screen" />;
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-32 -top-32 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-cyan/10 blur-3xl" />
        <div className="absolute left-1/2 top-1/3 h-64 w-64 -translate-x-1/2 rounded-full bg-primary/5 blur-3xl" />
      </div>

      <Card className="relative w-full max-w-md border-border/50 bg-card/80 backdrop-blur-xl">
        <CardHeader className="items-center text-center">
          <AIBoostLogo className="mb-4" />
          <CardDescription className="text-muted-foreground">
            Boost your AI edge.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={onSubmit} className="flex flex-col gap-4" data-lpignore="true">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Address</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        autoComplete="email"
                        required
                        placeholder="Enter your email"
                        className="bg-background/50"
                        data-lpignore="true"
                        data-form-type="other"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        autoComplete="current-password"
                        required
                        placeholder="Enter your password"
                        className="bg-background/50"
                        data-lpignore="true"
                        data-form-type="other"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm font-normal text-foreground">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-primary bg-background/50 accent-primary"
                  />
                  Remember me
                </label>
                <Link
                  to="/auth/forgot-password"
                  className="text-sm font-medium text-primary hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-primary to-primary/80 font-semibold shadow-lg shadow-primary/20 transition-all hover:shadow-primary/30"
                disabled={login.isPending}
              >
                {login.isPending ? "Please wait..." : "Sign In"}
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                First time signing in?{" "}
                <Link
                  to="/auth/forgot-password"
                  className="font-medium text-primary hover:underline"
                >
                  Set your password
                </Link>
              </p>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}

function AIBoostLogo({ className = "" }: { className?: string }) {
  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      <img src={shiftLogoInk} alt="AI Boost logo" className="h-10 w-auto object-contain dark:hidden" />
      <img src={shiftLogoWhite} alt="AI Boost logo" className="hidden h-10 w-auto object-contain dark:block" />
      <div className="font-sans text-3xl font-bold tracking-tight">
        <span className="text-foreground">AI</span>{" "}
        <span className="text-foreground/80">Boost</span>
      </div>
    </div>
  );
}
